import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import DiscountForm, { DiscountFormValues } from "../components/DiscountForm";
import { getDiscountById } from "../../../services/Discount/discount.service";
import { toast } from "react-toastify";
import "./FormPage.css";

const toDateInput = (value?: string | null) => (value ? value.slice(0, 10) : "");

// Helper to convert entity IDs to prefixed values
const getPrefixedValues = (entityType: string, entityIds: number[]): string[] => {
  if (!entityType || !entityIds || entityIds.length === 0) return [];
  return entityIds.map(id => `${entityType}_${id}`);
};

const EditDiscount: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const discountId = Number(id);
  const [loading, setLoading] = useState(true);
  const [initialValues, setInitialValues] = useState<DiscountFormValues | null>(null);

  useEffect(() => {
    const fetchDiscount = async () => {
      try {
        // ✅ Check if we have state data
        const stateData = location.state?.discountData;
        
        if (stateData) {
          // ✅ Get entity IDs from state
          const entityIds = stateData.entityIds || [];
          const entityType = stateData.entityType || "";
          
          // ✅ Convert to prefixed values for MultiSelect
          const entityValues = getPrefixedValues(entityType, entityIds);
          
          const values: DiscountFormValues = {
            name: stateData.discountName || stateData.name || "",
            description: stateData.description || "",
            startDate: toDateInput(stateData.startDate),
            endDate: toDateInput(stateData.endDate),
            type: stateData.valueType === "flat" || stateData.valueType === "flatAmount" 
              ? "flatAmount" 
              : "percentage",
            entityType: entityType as any,
            entityValues: entityValues, // ✅ Pass as array, not string
            value: stateData.value || "",
            minOrder: stateData.minOrderValue !== null && stateData.minOrderValue !== undefined
              ? String(stateData.minOrderValue)
              : "",
            maxDiscount: stateData.maxDiscount !== null && stateData.maxDiscount !== undefined
              ? String(stateData.maxDiscount)
              : "",
          };
          
          console.log("Initial Values from State:", values);
          setInitialValues(values);
          setLoading(false);
          return;
        }

        // ✅ Fetch from API if no state data
        const response = await getDiscountById(discountId);
        const data = response;
        
        // ✅ Get entity IDs from API response
        const entityIds = data.entityIds || [];
        const entityType = data.entityType || "";
        
        // ✅ Convert to prefixed values for MultiSelect
        const entityValues = getPrefixedValues(entityType, entityIds);
        
        const values: DiscountFormValues = {
          name: data.discountName || "",
          description: data.description || "",
          startDate: toDateInput(data.startDate),
          endDate: toDateInput(data.endDate),
          type: data.valueType === "flat" || data.valueType === "flatAmount" 
            ? "flatAmount" 
            : "percentage",
          entityType: entityType as any,
          entityValues: entityValues, // ✅ Pass as array, not string
          value: "",
          minOrder: data.minOrderValue !== null ? String(data.minOrderValue) : "",
          maxDiscount: data.maxDiscount !== null ? String(data.maxDiscount) : "",
        };
        
        console.log("Initial Values from API:", values);
        setInitialValues(values);
      } catch (error: any) {
        console.error("Error fetching discount:", error);
        toast.error(error?.message || "Failed to load discount details");
        navigate("/discount/list");
      } finally {
        setLoading(false);
      }
    };

    fetchDiscount();
  }, [discountId, location.state, navigate]);

  if (loading) {
    return (
      <div className="form-page">
        <div className="loading-container">
          <div className="loader">Loading...</div>
        </div>
      </div>
    );
  }

  if (!initialValues) {
    return (
      <div className="form-page">
        <div className="error-container">
          <p>Failed to load discount data. Please try again.</p>
          <button onClick={() => navigate("/discount/list")}>Go Back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="form-page">
      <div className="form-page-header">
        <h2>Edit Discount</h2>
        <button className="ghost-btn" onClick={() => navigate(-1)}>
          Back
        </button>
      </div>
      <DiscountForm
        mode="update"
        discountId={discountId}
        submitLabel="Update"
        initialValues={initialValues}
        onSuccess={() => navigate("/discount/list")}
      />
    </div>
  );
};

export default EditDiscount;